import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Plus, Trash2, Save, Wand2, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { notifyLaafficChanged } from "@/components/laaffic/LaafficUnmappedBanner";

/**
 * Laaffic disposition translation table.
 *
 * Laaffic tags each CDR with a free-text `labelName` (their wrap-up
 * call label). Our analytics + credit engine only understand a small
 * canonical vocab ("wrong number", "no answer", "interested", …).
 * This page is the bridge: one row per Laaffic label → the canonical
 * disposition_name it should be written as when we ingest CDRs.
 *
 * Workflow:
 *   1. Click "Auto-seed" and pick a team + date range.
 *   2. Dry-run first — see which labels we'd insert and their guesses.
 *   3. Commit — we insert unmapped rows. Labels we couldn't guess
 *      arrive with a blank canonical; you fill them in.
 *   4. Any label without a completed canonical is treated as
 *      unmapped by the backfill and live-webhook ingest — never
 *      silently written to ops_call_rows.
 */
const CANONICAL_OPTIONS = [
  "wrong number", "invalid number",
  "no answer", "unreachable", "busy",
  "voicemail", "language barrier",
  "interested", "not interested",
  "call back", "follow up required",
  "reactivated", "sms sent", "whatsapp sent", "untagged",
];

const CREDIT_OPTIONS = [
  { value: "", label: "— (let credit engine decide)" },
  { value: "yes", label: "yes (call credit)" },
  { value: "sms", label: "sms (indirect credit)" },
  { value: "unsure", label: "unsure" },
  { value: "no", label: "no (never credit)" },
];

export default function LaafficDispositionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [savingRow, setSavingRow] = useState({});

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/integrations/laaffic/disposition-map");
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const removeRow = async (id) => {
    if (!window.confirm("Delete this mapping? Any CDRs referencing this label will become unmapped.")) return;
    try {
      await api.delete(`/admin/integrations/laaffic/disposition-map/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const patchRow = async (row, patch) => {
    setSavingRow((s) => ({ ...s, [row.id]: true }));
    try {
      const { data } = await api.patch(`/admin/integrations/laaffic/disposition-map/${row.id}`, patch);
      setItems((prev) => prev.map((r) => (r.id === row.id ? data : r)));
      notifyLaafficChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setSavingRow((s) => ({ ...s, [row.id]: false }));
    }
  };

  const stats = useMemo(() => {
    const filled = items.filter((r) => r.canonical_disposition && r.canonical_disposition.trim()).length;
    return {
      total: items.length,
      filled,
      unmapped: items.length - filled,
    };
  }, [items]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="laaffic-dispositions-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Laaffic disposition mapping</h1>
          <div className="text-sm text-slate-600 mt-1 max-w-2xl">
            Bridge Laaffic&apos;s free-text disposition (from <code className="mx-1 px-1 py-0.5 bg-slate-100 rounded text-xs">remark</code> / <code className="mx-1 px-1 py-0.5 bg-slate-100 rounded text-xs">labelName</code>)
            tags to our canonical <code className="mx-1 px-1 py-0.5 bg-slate-100 rounded text-xs">disposition_name</code> vocab.
            Any label missing a canonical is treated as <b>unmapped</b> by ingest — CDRs with that label are quarantined,
            never silently written.
          </div>
          <div className="mt-2 flex gap-2 text-xs">
            <Link to="/admin/integrations" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Credentials
            </Link>
            <Link to="/admin/laaffic-parity" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Parity report
            </Link>
            <Link to="/admin/laaffic-backfill" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Backfill
            </Link>
            <Link to="/admin/laaffic-unmapped" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Unmapped queue
            </Link>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="disp-reload">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSeedOpen(true)} data-testid="disp-seed-open">
            <Wand2 className="w-4 h-4 mr-1" /> Auto-seed
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="disp-add-open">
            <Plus className="w-4 h-4 mr-1" /> Add mapping
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total mappings" value={stats.total} />
        <Stat label="Filled" value={stats.filled} tone="ok" />
        <Stat label="Unmapped / blank canonical" value={stats.unmapped} tone={stats.unmapped ? "warn" : "muted"} />
      </div>

      {err && (
        <div className="glass-card p-3 text-sm text-red-700 border-l-4 border-red-500" data-testid="disp-err">
          {err}
        </div>
      )}

      {showAdd && (
        <AddMappingForm
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {seedOpen && (
        <SeedModal onClose={() => setSeedOpen(false)} onDone={() => { setSeedOpen(false); load(); }} />
      )}

      <div className="glass-card overflow-hidden" data-testid="disp-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-slate-500 text-left">
              <th className="px-3 py-2">Laaffic label</th>
              <th className="px-3 py-2">Canonical disposition</th>
              <th className="px-3 py-2">Credit override</th>
              <th className="px-3 py-2 text-right">Sample count</th>
              <th className="px-3 py-2">Notes</th>
              <th className="px-3 py-2 text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                No mappings yet. Click <b>Auto-seed</b> to draft them from your recent Laaffic CDRs.
              </td></tr>
            )}
            {items.map((row) => (
              <MappingRow
                key={row.id}
                row={row}
                saving={!!savingRow[row.id]}
                onPatch={(p) => patchRow(row, p)}
                onDelete={() => removeRow(row.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = tone === "ok" ? "text-emerald-700"
              : tone === "warn" ? "text-amber-700"
              : tone === "muted" ? "text-slate-400" : "text-slate-900";
  return (
    <div className="glass-card p-3" data-testid={`disp-stat-${label.replace(/\W+/g, "-").toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

function MappingRow({ row, saving, onPatch, onDelete }) {
  const [canonical, setCanonical] = useState(row.canonical_disposition || row.suggested_canonical || "");
  const [override, setOverride] = useState(row.credit_status_override || "");
  const [notes, setNotes] = useState(row.notes || "");
  const dirty = canonical !== (row.canonical_disposition || "")
             || override !== (row.credit_status_override || "")
             || notes !== (row.notes || "");
  const filled = !!canonical.trim();

  return (
    <tr className={`border-t border-slate-100 ${!row.canonical_disposition ? "bg-amber-50/50" : ""}`}
        data-testid={`disp-row-${row.id}`}>
      <td className="px-3 py-2 font-mono text-xs">
        {row.laaffic_label}
        {!row.canonical_disposition && (
          <div className="text-[10px] text-amber-700 mt-0.5" data-testid={`disp-row-warning-${row.id}`}>
            {row.suggested_canonical && canonical === row.suggested_canonical
              ? "New tag from Laaffic — suggestion filled in, click save to confirm"
              : "Not mapped — calls with this tag are waiting"}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          list={`canon-options-${row.id}`}
          value={canonical}
          onChange={(e) => setCanonical(e.target.value)}
          placeholder="e.g. wrong number"
          className="glass-input w-full h-8 text-xs"
          data-testid={`disp-row-canonical-${row.id}`}
        />
        <datalist id={`canon-options-${row.id}`}>
          {CANONICAL_OPTIONS.map((o) => <option key={o} value={o} />)}
        </datalist>
      </td>
      <td className="px-3 py-2">
        <select
          value={override || ""}
          onChange={(e) => setOverride(e.target.value)}
          className="glass-input w-full h-8 text-xs"
          data-testid={`disp-row-override-${row.id}`}
        >
          {CREDIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-xs">{row.sample_count}</td>
      <td className="px-3 py-2">
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="—"
          className="glass-input w-full h-8 text-xs"
          data-testid={`disp-row-notes-${row.id}`}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex gap-1.5">
          <Button
            size="sm"
            variant="outline"
            disabled={(!dirty && !!row.canonical_disposition) || !filled || saving}
            onClick={() => onPatch({
              canonical_disposition: canonical,
              credit_status_override: override,
              notes,
            })}
            data-testid={`disp-row-save-${row.id}`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete} data-testid={`disp-row-del-${row.id}`}>
            <Trash2 className="w-3 h-3 text-red-600" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function AddMappingForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    laaffic_label: "",
    canonical_disposition: "",
    credit_status_override: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      await api.post("/admin/integrations/laaffic/disposition-map", form);
      notifyLaafficChanged();
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const bind = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4"
         onClick={onClose} data-testid="disp-add-overlay">
      <form onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-white rounded-lg shadow-xl max-w-md w-full mt-16 p-5 space-y-3"
            data-testid="disp-add-form">
        <div className="font-outfit font-semibold text-slate-900 text-lg">Add mapping</div>

        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Laaffic label *</label>
          <input required value={form.laaffic_label} onChange={bind("laaffic_label")}
                 placeholder="e.g. Reactivated"
                 className="glass-input w-full h-9 mt-1 font-mono text-xs"
                 data-testid="disp-add-label" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Canonical disposition *</label>
          <input list="canon-options-add" required
                 value={form.canonical_disposition} onChange={bind("canonical_disposition")}
                 placeholder="e.g. reactivated"
                 className="glass-input w-full h-9 mt-1"
                 data-testid="disp-add-canonical" />
          <datalist id="canon-options-add">
            {CANONICAL_OPTIONS.map((o) => <option key={o} value={o} />)}
          </datalist>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Credit override</label>
          <select value={form.credit_status_override} onChange={bind("credit_status_override")}
                  className="glass-input w-full h-9 mt-1"
                  data-testid="disp-add-override">
            {CREDIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Leave blank to let the credit engine decide from disposition + deposit history.
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500">Notes</label>
          <input value={form.notes} onChange={bind("notes")}
                 className="glass-input w-full h-9 mt-1"
                 data-testid="disp-add-notes" />
        </div>

        {err && <div className="text-sm text-red-600" data-testid="disp-add-err">{err}</div>}

        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="disp-add-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    : <><Save className="w-4 h-4 mr-2" /> Save</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

function SeedModal({ onClose, onDone }) {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({
    team_id: "",
    date_from: _defaultFrom(),
    date_to: _defaultTo(),
    task_id: "",
  });
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {}); }, []);

  const bind = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const runSeed = async (dry_run) => {
    setRunning(true);
    setErr("");
    if (dry_run) setPreview(null);
    try {
      const body = {
        team_id: form.team_id,
        date_from: form.date_from,
        date_to: form.date_to,
        task_id: form.task_id.trim() || null,
        dry_run,
      };
      const { data } = await api.post("/admin/integrations/laaffic/disposition-map/seed", body);
      setPreview(data);
      if (!dry_run) {
        alert(`Committed. Inserted ${data.stats.would_insert} new mapping(s). ${data.stats.unguessed} still need a canonical filled in.`);
        onDone();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4"
         onClick={onClose} data-testid="disp-seed-overlay">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl max-w-2xl w-full mt-16 p-5 space-y-3"
           data-testid="disp-seed-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-outfit font-semibold text-slate-900 text-lg">Auto-seed from parity window</div>
            <div className="text-xs text-slate-500 mt-0.5 max-w-md">
              Pulls Laaffic CDRs for the chosen team + range, extracts every distinct disposition
              (from <code>remark</code>/<code>labelName</code>), and drafts one mapping per new label
              with a best-guess canonical.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">Team *</label>
            <select required value={form.team_id} onChange={bind("team_id")}
                    className="glass-input w-full h-9 mt-1" data-testid="disp-seed-team">
              <option value="">— pick a team —</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">Task ID (optional)</label>
            <input value={form.task_id} onChange={bind("task_id")}
                   className="glass-input w-full h-9 mt-1 font-mono text-xs"
                   placeholder="filter to one campaign"
                   data-testid="disp-seed-task" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">From</label>
            <input type="date" value={form.date_from} onChange={bind("date_from")}
                   className="glass-input w-full h-9 mt-1" data-testid="disp-seed-from" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">To</label>
            <input type="date" value={form.date_to} onChange={bind("date_to")}
                   className="glass-input w-full h-9 mt-1" data-testid="disp-seed-to" />
          </div>
        </div>

        <div className="flex gap-2">
          <Button disabled={running || !form.team_id} onClick={() => runSeed(true)}
                  variant="outline" data-testid="disp-seed-dryrun">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Preview (dry-run)
          </Button>
          <Button disabled={running || !preview || !preview.stats?.would_insert}
                  onClick={() => runSeed(false)} data-testid="disp-seed-commit">
            Commit {preview?.stats?.would_insert ? `(${preview.stats.would_insert} new)` : ""}
          </Button>
        </div>

        {err && <div className="text-sm text-red-600" data-testid="disp-seed-err">{err}</div>}

        {preview && (
          <div className="space-y-2" data-testid="disp-seed-preview">
            {preview.fetch_error && (
              <div className="text-xs bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-2">
                Laaffic returned an error: {preview.fetch_error}
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
              <MiniStat label="CDRs pulled" value={preview.stats.pulled} />
              <MiniStat label="Distinct labels" value={preview.stats.distinct_labels} />
              <MiniStat label="New (would insert)" value={preview.stats.would_insert} tone="ok" />
              <MiniStat label="Guessed" value={preview.stats.guessed} tone="ok" />
              <MiniStat label="Blank canonical" value={preview.stats.unguessed} tone={preview.stats.unguessed ? "warn" : undefined} />
            </div>
            <div className="max-h-80 overflow-auto glass-card p-0">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5">Laaffic label</th>
                    <th className="px-2 py-1.5 text-right">Count</th>
                    <th className="px-2 py-1.5">Status</th>
                    <th className="px-2 py-1.5">Guess</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows || []).map((r) => (
                    <tr key={r.laaffic_label} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{r.laaffic_label}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{r.count}</td>
                      <td className="px-2 py-1">
                        {r.existing ? (
                          <span className="text-slate-500">existing → <b>{r.existing_canonical || "—"}</b></span>
                        ) : r.guess_canonical ? (
                          <span className="text-emerald-700">new → guessed</span>
                        ) : (
                          <span className="text-amber-700">new → needs canonical</span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {!r.existing && r.guess_canonical && (
                          <span className="font-mono">{r.guess_canonical}
                            {r.guess_credit_override ? ` (${r.guess_credit_override})` : ""}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-700"
           : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="glass-card p-2">
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-base font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function _defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 3);
  return d.toISOString().slice(0, 10);
}
function _defaultTo() {
  return new Date().toISOString().slice(0, 10);
}
